import React from 'react';
import { ChildProps, DataValue, withApollo, WithApolloClient } from '@apollo/client/react/hoc';
import I18n from 'i18n-js';
import { differenceBy, flatten, get, isEmpty, isEqual, merge, uniq } from 'lodash';
import { browserHistory, withRouter, WithRouterProps } from 'react-router';
import { BumpDSACompliancePopover } from '@reverbdotcom/commons/src/components/bump_dsa_compliance_popover';
import {
  core_apimessages_Ad,
  core_apimessages_CollectionHeader_CollectionType,
  Core_Marketplace_CombinedMarketplaceSearchQuery,
  Core_Marketplace_CombinedMarketplaceSearchQueryVariables,
  reverb_personalization_SearchQuery,
  reverb_search_ListingsSearchRequest_Sort,
  reverb_signals_Signal_Group,
} from '@reverbdotcom/commons/src/gql/graphql';
import { getPage, getTotalPages } from '../../pagination_helpers';
import CurationModal from '../discovery/curation/modal';
import ListingGrid, { ListingProps } from '../listing_grid';
import { listingsFilters, selectedFilters } from '../listings_filters';
import LowResultsAlert from '../low_results_alert';
import NoResultsAlert from '../no_results_alert';
import URLHelpers, { MarketplacePathContext, Paths } from '../shared/url_helpers';
import { SubqueryRows } from '../subquery_rows';
import HeaderWithMetadata from './header_with_metadata';
import {
  combinedMarketplaceSearchQuery,
  getCombinedMarketplaceInputs,
  includePromotedRows,
  mapParams,
  onlyQuery,
  PageSettings,
  usingSignalSystemMarketplace,
} from './map_params';
import { applyAutodirect, autoselectedFilters, updateMarketplaceUrl } from './marketplace_autodirects';
import MarketplaceFallbackListings from './marketplace_fallback_listings';
import { pageViewTrackedExperiments } from './page_view_tracking_data';

import bind from '@reverbdotcom/commons/src/bind';
import { AdBanner } from '@reverbdotcom/commons/src/components/ads/ad_banner';
import { adBannerMparticleData } from '@reverbdotcom/commons/src/components/ads/ad_mparticle_tracking_helpers';
import { LIST_VIEW_STATE } from '@reverbdotcom/commons/src/components/list_view_toggle';
import RQLSearchGrid, { PaginationMode } from '@reverbdotcom/commons/src/components/rql_search_grid';
import ScrollTarget from '@reverbdotcom/commons/src/components/scroll_target';
import { IUser, IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { BRAND_NEW } from '@reverbdotcom/commons/src/condition_helpers';
import { connect } from '@reverbdotcom/commons/src/connect';
import { EVERYWHERE_CODE, US_CON, currencies } from '@reverbdotcom/commons/src/constants';
import { trackCriteoProductList } from '@reverbdotcom/commons/src/criteo';
import * as elog from '@reverbdotcom/commons/src/elog';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { MParticleEventName, MParticlePageName, trackEvent, trackPageView } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { searchResultsSprigEvent } from '@reverbdotcom/commons/src/elog/sprig_events';
import { addOnExitTriggerForEmailPopUp, EmailPopUpModal } from '@reverbdotcom/commons/src/email_pop_up_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';
import { getAllSelectedOptions, getFiltersForSidebar } from '@reverbdotcom/commons/src/grid_helpers';
import { isGreatValue } from '@reverbdotcom/commons/src/listing_great_value';
import { getItem, setItem } from '@reverbdotcom/commons/src/local_storage_utils';
import { isBrandPage, isOutletHub, isShopPage, isUsingProximityFeatures } from '@reverbdotcom/commons/src/url_helpers';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import MarketplaceRelatedSearches from '../discovery/related_searches';
import { FavoriteButtonContainer } from '../favorites/favorite_button_container';
import withQuerySuggestions from '../with_query_suggestions';
import CombinedBumpListingGrid from './combined_bump_listing_grid';
import { MarketplaceProviders } from './marketplace_providers';
import { withUrlContext, WithUrlProps } from '@reverbdotcom/commons/src/url_context';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { HoistFavoriteCallout } from '../favorites/hoist_favorite_callout';
import { FavoriteButtonBanner } from '../favorites/favorite_button_banner';

interface RouterLocationProps {
  params: MarketplacePathContext;
}

interface AdWrapperProps {
  ad?: core_apimessages_Ad;
  adLoading?: boolean;
}

interface IExternalProps extends RouterLocationProps, AdWrapperProps {
  pageName: MParticlePageName;
  emptyGrid?: JSX.Element;
  showSold?: boolean;
  loading?: boolean;
  showFlags?: boolean;
  includeItemListMetadata?: boolean;
  saleType?: string;
}

interface QuerySuggestionsProps {
  querySuggestions?: reverb_personalization_SearchQuery[];
  suggestionsLoading?: boolean;
}

interface PropsForQuery extends IUserContext, RouterLocationProps, WithUrlProps, AdWrapperProps {
  pageName?: MParticlePageName;
  showSold?: boolean;
  saleType?: string;
}

interface IState {
  listViewEnabled: boolean;
  openModal: boolean;
  exposedSidebarOpen: boolean;
}

type ICombinedProps = IUserContext & WithRouterProps & WithApolloClient<IExternalProps> & QuerySuggestionsProps & WithUrlProps;

export type MPProps = ChildProps<ICombinedProps, Core_Marketplace_CombinedMarketplaceSearchQuery, Core_Marketplace_CombinedMarketplaceSearchQueryVariables>;
type MPQueryProps = React.PropsWithChildren<ChildProps<PropsForQuery, Core_Marketplace_CombinedMarketplaceSearchQuery, Core_Marketplace_CombinedMarketplaceSearchQueryVariables>>;

const LOW_LISTINGS_THRESHOLD = 10;

interface PageConfigs { [k: string]: PageSettings }

const generatePageSettings = (params: MarketplacePathContext, showSold: boolean): PageConfigs => ({
  [MParticlePageName.Marketplace]: {
    pageName: MParticlePageName.Marketplace,
    useExperimentalRecall: true,
  },
  [MParticlePageName.Brand]: {
    pageName: MParticlePageName.Brand,
    useExperimentalRecall: false,
    renderCollectionHeaderDescription: true,
    collectionSlug: params.brand_slug,
    collectionType: core_apimessages_CollectionHeader_CollectionType.Brand,
  },
  [MParticlePageName.Shop]: {
    pageName: MParticlePageName.Shop,
    useExperimentalRecall: false,
    showSold,
    pinTextQuery: true,
  },
  [MParticlePageName.Holiday]: {
    pageName: MParticlePageName.Holiday,
    useExperimentalRecall: false,
    hideFollowButtons: true,
    holidaySale: true,
    skipHeader: true,
  },
  [MParticlePageName.Sale]: {
    pageName: MParticlePageName.Sale,
    useExperimentalRecall: false,
    hideFollowButtons: true,
    collectionSlug: params.sale_slug,
    collectionType: core_apimessages_CollectionHeader_CollectionType.CuratedSet,
    skipHeader: true,
  },
  [MParticlePageName.Handpicked]: {
    pageName: MParticlePageName.Handpicked,
    useExperimentalRecall: false,
    renderCollectionHeaderDescription: true,
    collectionSlug: params.curated_set_slug,
    collectionType: core_apimessages_CollectionHeader_CollectionType.CuratedSet,
  },
});

function shouldFilterFeaturedWinnerRanks(queryParams: any, user: IUser, pageName: string): boolean {
  return !queryParams.sort &&
    !queryParams.show_only_sold &&
    !queryParams.condition &&
    user.shippingRegionCode === US_CON &&
    user.currency === currencies.USD &&
    pageName !== MParticlePageName.Shop;
}

export class MarketplacePage extends React.Component<MPProps, IState> {
  state: IState = {
    listViewEnabled: getItem(LIST_VIEW_STATE),
    openModal: false,
    exposedSidebarOpen: false,
  };

  scrollTarget: ScrollTarget;

  @bind
  setListViewState(enabled: boolean) {
    setItem(LIST_VIEW_STATE, enabled);
    this.setState(
      { listViewEnabled: enabled },
      () => this.data.refetch({
        usingListView: enabled,
        shouldntLoadBumps: false,
        shouldntLoadSuggestions: false,
        useSignalSystem: usingSignalSystemMarketplace(),
        signalGroups: getSignalGroups(this.props.url, enabled),
      }),
    );
  }

  @bind
  setOpenModal(isOpen: boolean) {
    this.setState({ openModal: isOpen });
  }

  componentDidMount() {
    if (this.isMarketplacePage()) {
      this.trackAdExperimentQualifyingEvent();
    }

    this.addEmailPopUpTriggerIfAllowedPage();
  }

  componentDidUpdate(prevProps: MPProps) {
    if (prevProps.url.query?.page !== this.props.url.query?.page) {
      this.scrollTarget.scrollToTop();
    }

    const isSearchLoaded = !this.data.loading;
    const isSearchUpdated = !isEqual(prevProps.data.listingsSearch, this.data.listingsSearch);

    const shouldTrackPageView = isSearchLoaded && isSearchUpdated;
    if (shouldTrackPageView) {
      this.trackPageView();

      const { user } = this.props;
      if (user && !user.loggedOut && isExperimentEnabled(user, experiments.WEB_CSAT)) {
        searchResultsSprigEvent(this.data.listingsSearch);
      }
    }

    const shouldTrackAdExperimentQualifyingEvent = !this.isAdOrSearchLoading() && isSearchUpdated && this.isMarketplacePage();
    if (shouldTrackAdExperimentQualifyingEvent) {
      this.trackAdExperimentQualifyingEvent();
    }

    applyAutodirect({ prevProps, props: this.props });

    // Have brand and brand model redirects been fully replaced by auto directs? Can these be removed?
    const shouldStripBrandModelRedirectParam = isSearchLoaded && this.isBrandModelRedirected();
    if (shouldStripBrandModelRedirectParam) {
      this.stripRedirectParam('brand_model_redirected');
    }

    const shouldStripBrandRedirectParam = isSearchLoaded && this.isBrandRedirected();
    if (shouldStripBrandRedirectParam) {
      this.stripRedirectParam('brand_redirected');
    }
  }

  stripRedirectParam(param) {
    const { url } = this.props;
    delete url.query[param];
    browserHistory.push(url);
  }

  isMarketplacePage() {
    return this.props.pageName === MParticlePageName.Marketplace;
  }

  pageSettings(): PageSettings {
    const { params, showSold, pageName } = this.props;

    const pageConfigs = generatePageSettings(params, showSold);

    return pageConfigs[pageName];
  }

  isBrandRedirected() {
    return this.props.url.query.brand_redirected === 'true';
  }

  isBrandModelRedirected() {
    return this.props.url.query.brand_model_redirected === 'true';
  }

  /**
   * `this.data` returns a data object where listingsSearch, aggsSearch, and bumpedSearch results are always
   * available, even if stale. while loading new results, we display old results in the grid and filters with loading
   * ui via css, so this method ensures that previous results are present for that loading state.
   */
  get data(): DataValue<Core_Marketplace_CombinedMarketplaceSearchQuery, Core_Marketplace_CombinedMarketplaceSearchQueryVariables> {
    const safeData = this.props.data as any || {};
    const safePreviousData = safeData.previousData || {};

    return {
      ...safePreviousData,
      ...safeData,
    };
  }

  /**
   * `isAdOrSearchLoading()` was previously named `loading()`, and was renamed for clarity.
   *
   * Ad data and listings search data are always fetched in different queries,
   * and are not guaranteed to resolve their loading states during the same render cycle.
   *
   * `this.data` is resolved from the `Core_Marketplace_CombinedMarketplaceSearch` query and the `MarketplacePage` component.
   * `this.props.adLoading` and `this.props.ad` are resolved from the `Core_Ad_Banner_Wrapper` query and the `MarketplacePageWrapper` component.
   */
  isAdOrSearchLoading() {
    return this.props.adLoading || isEmpty(this.data) || this.data.loading;
  }

  searchResult(): Core_Marketplace_CombinedMarketplaceSearchQuery['listingsSearch'] {
    return this.data?.listingsSearch;
  }

  bumpSearchResult(): Core_Marketplace_CombinedMarketplaceSearchQuery['listingsSearch'] {
    return this.data?.bumpedSearch;
  }

  correctedTo() {
    return this.data?.listingsSearch?.correctedTo;
  }

  correctedFrom() {
    return this.data?.listingsSearch?.correctedFrom;
  }

  filters() {
    const oldOrNewFilters = this.data?.aggsSearch?.filters;
    const filters = oldOrNewFilters || [];

    return listingsFilters(filters, this.props.url);
  }

  selectedFilters() {
    const oldOrNewFilters = this.data?.aggsSearch?.filters;
    const filters = oldOrNewFilters || [];
    return selectedFilters(filters);
  }

  getQuery(props: MPProps) {
    return props.data?.aggsSearch?.filters.find(f => f.key === 'TEXT_QUERY')?.options[0]?.optionValue;
  }

  getClearFilterUrl() {
    const { pathname, query } = this.props.url;
    const clearFilterUrl = pathname?.includes('/marketplace') ? Paths.marketplace.expand({ query: query?.query }) : pathname;
    return clearFilterUrl;
  }

  getTotalText() {
    const count = this.searchResult()?.total || 0;

    return I18n.t(
      'discovery.resultsCount.countListings',
      {
        count,
        num: I18n.toNumber(count, { precision: 0 }),
      },
    );
  }

  getListings() {
    return this.searchResult()?.listings || [];
  }

  getBumpedListings() {
    return this.bumpSearchResult()?.listings || [];
  }

  defaultSort() {
    let defaultSort;
    const filters = this.filters();
    const sortFilter = filters.find(({ key }) => key === 'SORT_BY');
    if (sortFilter) {
      defaultSort = sortFilter.options.find(({ selected }) => !!selected)?.trackingValue;
    }
    return defaultSort || reverb_search_ListingsSearchRequest_Sort.NONE;
  }

  getFallbackListings() {
    const fallback = this.searchResult()?.fallbackListings || [];
    const listings = this.getListings();

    if (listings.length === 0) return fallback;

    return differenceBy(fallback, listings, 'id');
  }

  getSuggestedQuery() {
    const queries = this.searchResult()?.suggestedQueries || [];

    return queries?.[0];
  }

  shouldShowFallbackListings() {
    return this.getFallbackListings().length > 0;
  }

  shouldDisplayRelatedSearches() {
    return this.isMarketplacePage() && !!this.props.url?.query?.query;
  }

  renderRelatedSearchRow() {
    if (!this.shouldDisplayRelatedSearches()) {
      return null;
    }

    return (
      <MarketplaceRelatedSearches
        querySuggestions={this.props.querySuggestions}
        loading={this.props.suggestionsLoading}
        relatedSearchLocation="top"
        key="related-searches-top-legacy-marketplace-page"
        displayPills
      />

    );
  }

  renderSecondaryRelatedSearchRow() {
    if (!this.shouldDisplayRelatedSearches() || this.data.error) {
      return null;
    }

    return (
      <MarketplaceRelatedSearches
        querySuggestions={this.props.querySuggestions}
        loading={this.props.suggestionsLoading}
        relatedSearchLocation="bottom"
        key="related-searches-bottom-legacy-marketplace-page"
      />
    );
  }

  renderFollowBanner() {
    if (isShopPage(this.props.url)) return null;
    if (this.pageSettings().hideFollowButtons) { return null; }
    if (this.isAdOrSearchLoading()) { return null; }
    if (!this.searchResult()) { return null; }
    if (this.shouldShowFallbackListings()) { return null; }
    const listings = this.getListings();
    const header = listings.length >= 5 ? I18n.t('discovery.favorites.banner.unsaved') : null;
    const subtext = listings.length >= 5 ? I18n.t('discovery.favorites.banner.notSaved.body.unfiltered') : null;

    return (
      <FavoriteButtonBanner
        header={header}
        subtext={subtext}
      />
    );
  }

  renderAdBanner() {
    const hasData = (!this.props.adLoading && !!this.props.ad?.imageAdCreative);
    if (!hasData) return null;
    // do not show ad banner if Shop Page
    if (this.pageSettings().pageName === MParticlePageName.Shop) return null;

    return (
      <div className="mt-6 mb-4 site-wrapper">
        <AdBanner
          ad={this.props.ad}
        />
      </div>
    );
  }

  shouldShowPromotedListings() {
    if (this.getListings().length === 0) return false;
    if (this.showingOnlySold()) return false;
    if (shouldFilterFeaturedWinnerRanks(this.props.url?.query, this.props.user, this.props.pageName)) return true;
    if (this.shouldShowFallbackListings()) return false;

    return includePromotedRows(this.props.params, this.props.url?.query);
  }

  didYouMeanSuggestion() {
    return this.searchResult()?.suggestions?.[0];
  }

  useExperimentalRecall(): boolean {
    return this.pageSettings().useExperimentalRecall || false;
  }

  emptyGridExperimentOverride() {
    if (this.useExperimentalRecall() && onlyQuery(this.props.url.query)) {
      return (
        <NoResultsAlert
          query={this.props.url?.query.query}
          suggestion={this.didYouMeanSuggestion()}
        />
      );
    }

    // overrides default empty grid if either experiment is active
    if (this.shouldShowFallbackListings()) return <div />;

    // uses default
    return this.props.emptyGrid;
  }

  hasLowResults() {
    return !this.data?.loading &&
      this.getListings().length <= LOW_LISTINGS_THRESHOLD;
  }

  hasNoResults() {
    return !this.data?.loading &&
      this.getListings().length === 0;
  }

  hasSort() {
    return !!this.props.url?.query?.sort;
  }

  onFirstPage() {
    const results = this.searchResult();

    return getPage(results.offset, results.limit) === 1;
  }

  showingOnlySold() {
    return !!this.props.url?.query?.show_only_sold;
  }

  wasReferredByTrendingSearches() {
    return this.props.url.query.referrer === 'trending_search';
  }

  lowResultsAlert() {
    if (this.wasReferredByTrendingSearches()) { return null; }
    if (onlyQuery(this.props.url.query) &&
      this.hasLowResults() &&
      !this.hasNoResults() &&
      this.useExperimentalRecall()) {
      return (
        <LowResultsAlert
          suggestion={this.didYouMeanSuggestion()}
          query={this.props.url?.query.query}
        />
      );
    }

    return null;
  }

  @bind
  toggleExposedSidebarOpen() {
    this.setState((prevState) => ({ exposedSidebarOpen: !prevState.exposedSidebarOpen }));
  }

  hasSelectedFilterOptions() {
    const filtersForSidebar = getFiltersForSidebar(this.filters());

    return !!getAllSelectedOptions(filtersForSidebar).length;
  }

  @bind
  clearFiltersForMobileSidebar() {
    this.props.router.push(this.getClearFilterUrl());
  }

  renderCards() {
    const trackingSort = this.props.url?.query?.sort || this.defaultSort();
    const results = this.searchResult();
    const trackingPage = results != undefined ? getPage(results.offset, results.limit) : 0;
    const isShowingPromotedListings = this.shouldShowPromotedListings();
    const listingProps = {
      isListView: this.state.listViewEnabled,
      loading: this.isAdOrSearchLoading(),
      listings: this.getListings(),
      trackingQuery: this.trackingQuery(),
      trackingFilter: this.selectedFilters(),
      trackingSort: trackingSort,
      trackingPage: trackingPage,
      pageSettings: this.pageSettings(),
      filters: this.filters(),
      exposedSidebarOpen: this.state.exposedSidebarOpen,
      clearFiltersDisabled: this.hasSelectedFilterOptions(),
      toggleExposedSidebarOpen: this.toggleExposedSidebarOpen,
      listingSearchTotal: results?.total,
      clearFilters: this.clearFiltersForMobileSidebar,
      emptyGrid: this.emptyGridExperimentOverride(),
    } as ListingProps;

    if (isShowingPromotedListings) {
      return (
        <CombinedBumpListingGrid
          {...listingProps} // eslint-disable-line react/jsx-props-no-spreading
          bumpedListings={this.getBumpedListings()}
          error={this.data.error}
          includeItemListMetadata={this.props.includeItemListMetadata}
        />
      );
    }

    return (
      <ListingGrid
        {...listingProps} // eslint-disable-line react/jsx-props-no-spreading
        error={!!this.data.error}
        showFlags={this.props.showFlags}
        includeItemListMetadata={this.props.includeItemListMetadata}
      />
    );
  }

  renderFallbackCards() {
    return (
      <MarketplaceFallbackListings
        fallbackListings={this.getFallbackListings()}
        query={this.props.url.query.query}
        hasPrimaryListings={this.getListings().length > 0}
        displayAsRow={this.useExperimentalRecall()}
      />
    );
  }

  renderSubqueryRows() {
    if (this.wasReferredByTrendingSearches()) { return null; }
    if (!this.useExperimentalRecall() || !this.hasLowResults()) return null;

    const { query } = this.props.url.query;

    return (
      <SubqueryRows
        query={query}
        params={this.props.params}
        listingsSearchCount={this.getListings().length}
      />
    );
  }

  trackingQuery() {
    return this.isBrandRedirected() ? this.props.url.query.make : this.props.url.query.query;
  }

  freeShippingListingIds() {
    const listings = this.getListings();
    return listings.filter(listing => listing.shipping?.shippingPrices?.[0]?.rate?.amountCents === 0).map(l => l.id);
  }

  greatValueListingIds() {
    const listings = this.getListings();
    return listings.filter(listing => isGreatValue(listing, this.props.user)).map(l => l.id);
  }

  returnPolicyListingIds() {
    const listings = this.getListings();
    return listings.filter(listing => {
      if (listing.soldAsIs) { return; }
      const returnPolicyDays = listing.condition.conditionSlug === BRAND_NEW ? listing.shop?.returnPolicy?.newReturnWindowDays : listing.shop?.returnPolicy?.usedReturnWindowDays;
      return !!returnPolicyDays && returnPolicyDays >= 14;
    }).map(l => l.id);
  }

  get signals() {
    const listings = this.getListings();
    const signals = listings?.map(
      listing => listing?.signals?.map(signal => signal.name),
    );

    const formattedSignals = uniq(flatten(signals)).join('|');

    return formattedSignals;
  }

  sharedTrackingData() {
    const { listViewEnabled } = this.state;
    const { user, url, params } = this.props;
    const queryParams = url?.query || {};
    const results = this.searchResult();
    const trackedExperiments = pageViewTrackedExperiments({
      user,
    });

    let correctedToString = this.correctedTo();
    let correctedFromString = this.correctedFrom();
    if (!correctedToString && results.total === 0) { correctedToString = this.didYouMeanSuggestion()?.text; }
    if (!correctedFromString && results.total === 0) { correctedFromString = this.props.url?.query?.query; }

    const trackingData = {
      pageName: this.pageSettings().pageName || MParticlePageName.Marketplace,
      experiments: formatExperimentsForEventAttributes(this.props.user, trackedExperiments),
      query: this.trackingQuery(),
      autodirected: autoselectedFilters(this.props).length !== 0,
      brandRedirected: this.isBrandRedirected(),
      brandModelRedirected: this.isBrandModelRedirected(),
      sort: queryParams.sort || this.defaultSort(),
      filter: this.selectedFilters(),
      listingIds: results.listings?.map(l => l.id),
      esScores: results.listings?.map(l => l.esScore),
      querySuggestion: this.getSuggestedQuery(),
      page: getPage(results.offset, results.limit),
      totalPages: getTotalPages(results.total, results.limit),
      listingsCount: results.total,
      fallbackListingsCount: this.getFallbackListings().length,
      productType: queryParams.product_type || '',
      make: queryParams.make || '',
      category: queryParams.category || '',
      shopSlug: params.shop || '',
      brand: params.brand_slug || '',
      curatedSetSlug: params.curated_set_slug || '',
      postalCode: queryParams.postal_code,
      distance: queryParams.distance,
      view: listViewEnabled ? 'List' : 'Grid',
      freeShippingListingIds: this.freeShippingListingIds(),
      returnPolicyListingIds: this.returnPolicyListingIds(),
      refererSlug: queryParams.referer,
      signals: this.signals,
      relatedSearchCount: this.props.querySuggestions?.length || 0,
      correctedTo: correctedToString || '',
      correctedFrom: correctedFromString || '',
    };

    if (this.props.user.shippingRegionCode === 'GB' && this.greatValueListingIds().length > 0) {
      merge(trackingData, { greatValueListingIds: this.greatValueListingIds() });
    }

    if (this.shouldShowPromotedListings()) {
      merge(trackingData, { bumpedListingIds: this.getBumpedListings()?.map(l => l.id) });
    }

    return trackingData;
  }

  trackPageView() {
    trackPageView({
      ...this.sharedTrackingData(),
    });

    const results = this.searchResult();

    trackCriteoProductList({
      listingIds: results.listings.map(l => l.id),
      keywords: this.trackingQuery(),
      page_number: getPage(results.offset, results.limit),
    }, this.props.user);
  }

  trackAdExperimentQualifyingEvent() {
    trackEvent({
      ...adBannerMparticleData(this.props.ad),
      ...{
        componentName: MParticlePageName.Marketplace,
        eventName: MParticleEventName.MarketplaceGridAdQE,
        experiments: formatExperimentsForEventAttributes(this.props.user, []),
      },
    });
  }

  @bind
  addEmailPopUpTriggerIfAllowedPage() {
    addOnExitTriggerForEmailPopUp(this.props.user, this.state.openModal, this.setOpenModal, this.props.url);
  }

  renderSellerLocationFilter(): boolean {
    return !isShopPage(this.props.url);
  }

  renderGridLevelFollowButton() {
    if (isShopPage(this.props.url)) {
      return null;
    }

    if (this.pageSettings().hideFollowButtons) {
      return null;
    }

    return (
      <HoistFavoriteCallout anchor={
        <span className="d-inline-block">
          {this.renderGridLevelFollowButtonContainer()}
        </span>
      }
      />
    );
  }

  renderGridLevelFollowButtonContainer() {
    return (
      <FavoriteButtonContainer
        componentName="FavoriteButton (SearchOverview)"
        displayStyle="link"
        iconButtonSize="mini"
        iconButtonVariant="transparent"
        condensed
        showCallout
      />
    );
  }

  tipText() {
    if (isShopPage(this.props.url)) {
      return null;
    }

    if (this.correctedFrom()) {
      const revertSearchUrl = URLHelpers.marketplacePath({
        ...this.props.location.query,
        skip_autocorrect: true,
        query: this.correctedFrom(),
      });
      const clickEvent = {
        eventName: MParticleEventName.ClickedAutocorrectOriginalQuery,
        originalQuery: this.correctedFrom(),
        correctedQuery: this.correctedTo(),
        componentName: MParticlePageName.Marketplace,
      };
      return (
        <div className="mb-3 tablet-mb-1">
          <div className="size-fixed-110">
            Search instead for
            <CoreLink
              to={revertSearchUrl}
              className="text-link ml-1"
              clickEvent={clickEvent}
            >
              {this.correctedFrom()}
            </CoreLink>
          </div>
        </div>
      );
    }

    return <BumpDSACompliancePopover />;
  }

  titleFollowText() {
    if (this.correctedFrom()) {
      return <BumpDSACompliancePopover />;
    }

    return null;
  }

  render() {
    const searchResult = this.searchResult();
    const hideClearFiltersButton = isEmpty(this.props.url?.query);
    if (this.data.error) {
      elog.error(
        'marketplace-render-failure',
        {
          networkErrorStack: this.data.error.networkError?.stack?.substring(0, 500),
        },
        this.data.error,
      );
    }

    return (
      <MarketplaceProviders
        updateUrl={(props) => updateMarketplaceUrl({ ...props, ...{ correctedTo: this.correctedTo() } })}
        keySuffix={String(this.state.listViewEnabled)}
      >
        {!this.pageSettings().skipHeader &&
          <HeaderWithMetadata
            params={this.props.params}
            query={this.correctedTo() || get(this.props, 'location.query', {})}
            collectionType={this.pageSettings()?.collectionType}
            collectionSlug={this.pageSettings()?.collectionSlug}
            renderCollectionHeaderDescription={this.pageSettings().renderCollectionHeaderDescription}
            isBrandPage={isBrandPage(this.props.url)}
          />
        }
        <ScrollTarget onlyScrollUp ref={r => this.scrollTarget = r} />
        <RQLSearchGrid
          error={!!this.data.error}
          loading={this.isAdOrSearchLoading()}
          search={searchResult}
          filters={this.filters()}
          location={this.props.url}
          aboveGridBanner={this.renderAdBanner()}
          relatedSearchRow={this.renderRelatedSearchRow()}
          secondaryRelatedSearchRow={this.renderSecondaryRelatedSearchRow()}
          totalText={this.getTotalText()}
          followButton={this.renderGridLevelFollowButton()}
          cards={this.renderCards()}
          fallbackCards={this.renderFallbackCards()}
          subqueryRows={this.renderSubqueryRows()}
          paginationMode={PaginationMode.PAGINATED}
          hasSearchOverview
          followBanner={this.renderFollowBanner()}
          trackingQuery={this.trackingQuery()}
          lowResultsAlert={this.lowResultsAlert()}
          setListViewState={this.setListViewState}
          showListViewToggle
          useH1Title={this.isMarketplacePage()}
          resultsTotal={searchResult?.total}
          query={this.correctedTo() || this.props.url?.query?.query}
          pinTextQuery={!!this.pageSettings().pinTextQuery}
          toggleExposedSidebarOpen={this.toggleExposedSidebarOpen}
          showNewMobileSidebar
          hideClearFiltersButton={hideClearFiltersButton}
          clearFiltersUrl={this.getClearFilterUrl()}
          tipText={this.tipText()}
          titleFollowText={this.titleFollowText()}
        />
        <CurationModal />
        <EmailPopUpModal
          openModal={this.state.openModal}
          setOpenModal={this.setOpenModal}
          location={this.props.url}
        />
      </MarketplaceProviders>
    );
  }
}

function getSignalGroups(location, usingListView) {
  const useProximitySignals = isUsingProximityFeatures(location);
  const signalGroups = [];
  if (usingListView) {
    signalGroups.push(useProximitySignals ? reverb_signals_Signal_Group.MP_LIST_PROX_CARD : reverb_signals_Signal_Group.MP_LIST_CARD);
  } else {
    signalGroups.push(useProximitySignals ? reverb_signals_Signal_Group.MP_GRID_PROX_CARD : reverb_signals_Signal_Group.MP_GRID_CARD);
  }

  return signalGroups;
}

export function mapVarsForMarketplaceQuery(
  props: MPQueryProps,
  bumpLimitOverride?: number,
  listingsLimitOverride?: number,
  queryOverride?: string,
  boostByBumpRateOverride?: boolean,
): Core_Marketplace_CombinedMarketplaceSearchQueryVariables {
  const { params, user, url, showSold, pageName, saleType } = props;

  const pageSettings = generatePageSettings(params, showSold)[pageName] || {};
  const pathParams = params;
  const queryParams = url?.query || {};
  const usingListView = getItem(LIST_VIEW_STATE) || false;

  const mapped = mapParams({
    pathParams,
    location: url,
    user,
    pageSettings: {
      holidaySale: pageSettings.holidaySale,
      showSold: pageSettings.showSold,
      useExperimentalRecall: pageSettings.useExperimentalRecall,
      pageName,
    },
    listingsLimitOverride,
    queryOverride,
    boostByBumpRateOverride,
    isOutlet: isOutletHub(url),
  });

  const shouldntLoadBumps = [
    pathParams.shop,
    pathParams.sale_slug && saleType == 'seller',
    queryParams.show_only_sold,
    queryParams.showsold,
    pageSettings.showSold,
    pageSettings.holidaySale,
    queryOverride,
  ].some(Boolean);

  return {
    // Variables for inputs
    ...getCombinedMarketplaceInputs({
      mapped,
      user,
      queryParams: url.query,
      bumpLimit: bumpLimitOverride,
      shouldFilterFeaturedWinnerRanks: shouldFilterFeaturedWinnerRanks(queryParams, user, pageName),
    }),
    // Variables for directives (https://graphql.org/learn/queries/#directives)
    shouldntLoadBumps,
    shouldntLoadSuggestions: false,
    usingListView,
    signalGroups: getSignalGroups(url, usingListView),
    useSignalSystem: usingSignalSystemMarketplace(),
  };
}

const withMarketplaceSearchQuery = withGraphql<
  ICombinedProps,
  Core_Marketplace_CombinedMarketplaceSearchQuery,
  Core_Marketplace_CombinedMarketplaceSearchQueryVariables
>(combinedMarketplaceSearchQuery, {
  options: (props) => {
    return {
      context: {
        headers: {
          ...(props.params.shop && { 'X-Item-Region': EVERYWHERE_CODE }),
        },
      },
      ssr: false,
      variables: mapVarsForMarketplaceQuery(props),
    };
  },
});

export default connect<IExternalProps>([
  withUserContext,
  withApollo,
  withRouter,
  withUrlContext,
  withMarketplaceSearchQuery,
  withQuerySuggestions,
])(MarketplacePage);
